import { useCallback } from "react";
import { Spinner } from "@/components/atoms/Spinner";

import ListPlayerCompareView from "./ListPlayerCompareView";
import PlayerCompareItem from "@/features/compare-players/components/molecules/PlayerCompareItem";

import { Text } from "@/components/atoms/Text";
import { type ListRenderItem } from "@/components/atoms/FlashList";
import { Stack } from "@/components/atoms/Stack";
import { PlayerCardData } from "@/types/playerCardTypes";
import useMobileScreen from "@/hooks/useMobileScreen";
import { ifWeb } from "@/utils/platform";

/**
 *
 */
interface IListPlayerCompare {
  onLoadMore?: (value: string) => void;
  data?: PlayerCardData[];
  keyList: string;
  loading: boolean;
  showsVertical?: boolean;
  title: string;
  onSelectComparePlayer: (player: PlayerCardData) => void;
}

/**
 * Container component for displaying a list of players available for comparison
 * Handles rendering player items, loading states, and empty states
 * @param root0 - Component props
 * @param root0.data - Array of players to display in the list
 * @param root0.keyList - Unique key identifier for the list (e.g., "watchlist", "myroster")
 * @param root0.loading - Whether the player list is currently loading
 * @param root0.title - Title displayed at the top of the list
 * @param root0.onLoadMore - Callback to load more players when scrolling to end
 * @param root0.onSelectComparePlayer - Callback when a player is selected for comparison
 * @param root0.showsVertical - Whether to show vertical scroll indicator
 */
const ListPlayerCompareContainer = ({
  data,
  keyList,
  loading,
  title,
  onLoadMore,
  onSelectComparePlayer,
  showsVertical = true,
}: IListPlayerCompare) => {
  const isSmallScreen = useMobileScreen();
  const renderPlayer: ListRenderItem<PlayerCardData> = useCallback(
    ({ item, index }) => (
      <PlayerCompareItem
        item={item}
        index={index}
        isSmallScreen={isSmallScreen}
        onSelectComparePlayer={onSelectComparePlayer}
      />
    ),
    [onSelectComparePlayer, isSmallScreen]
  );

  const keyExtractor = useCallback(
    (item: PlayerCardData) => `${keyList}_${item.id}`,
    [keyList]
  );

  const handleEndReached = useCallback(() => {
    onLoadMore?.(keyList);
  }, [onLoadMore, keyList]);

  const renderListFooterComponent = useCallback(() => {
    if (loading) {
      return (
        <Stack className="pt-4">
          <Spinner size="small" color="white" />
        </Stack>
      );
    }
    return null;
  }, [loading]);

  const renderListEmptyComponent = useCallback(() => {
    if (loading) {
      return null;
    }
    return (
      <Stack className="items-center justify-center gap-3">
        <Text variant={ifWeb("title-sm", "display-sm")} className="text-center">
          No Players
        </Text>
      </Stack>
    );
  }, [loading]);

  return (
    <ListPlayerCompareView
      data={data}
      title={title}
      renderPlayer={renderPlayer}
      keyExtractor={keyExtractor}
      onEndReached={handleEndReached}
      showsVertical={showsVertical}
      renderListEmptyComponent={renderListEmptyComponent}
      renderListFooterComponent={renderListFooterComponent}
    />
  );
};

export default ListPlayerCompareContainer;
